/**
 * @description Enum of supported Opteo platforms
 */
export const enum Platform {
    GoogleAds = 0,
    MicrosoftAds = 1,
    MetaAds = 2,
    LinkedInAds = 3,
    GoogleAnalytics = 4,
    TikTokAds = 5,
    GoogleSearchConsole = 6,
    ChatGptAds = 7,
}

export type AdPlatform = Exclude<Platform, Platform.GoogleAnalytics | Platform.GoogleSearchConsole>

/**
 * @description Platform name to 2 character prefix, used for Account.ID
 */
export const enum PlatformIdPrefix {
    GoogleAds = 'ga',
    MicrosoftAds = 'ms',
    MetaAds = 'me',
    LinkedInAds = 'li',
    GoogleAnalytics = 'ga4',
    TikTokAds = 'tt',
    GoogleSearchConsole = 'gsc',
    ChatGptAds = 'cg',
}

/**
 * @description Get the platform prefix from a specified platform identifier
 */
export const PlatformToPrefix = {
    [Platform.GoogleAds]: PlatformIdPrefix.GoogleAds,
    [Platform.MicrosoftAds]: PlatformIdPrefix.MicrosoftAds,
    [Platform.MetaAds]: PlatformIdPrefix.MetaAds,
    [Platform.LinkedInAds]: PlatformIdPrefix.LinkedInAds,
    [Platform.GoogleAnalytics]: PlatformIdPrefix.GoogleAnalytics,
    [Platform.TikTokAds]: PlatformIdPrefix.TikTokAds,
    [Platform.GoogleSearchConsole]: PlatformIdPrefix.GoogleSearchConsole,
    [Platform.ChatGptAds]: PlatformIdPrefix.ChatGptAds,
} as const

/**
 * @description Get the platform name from a specified platform identifier
 */
export const PlatformToName = {
    [Platform.GoogleAds]: 'google',
    [Platform.MicrosoftAds]: 'microsoft',
    [Platform.MetaAds]: 'meta',
    [Platform.LinkedInAds]: 'linkedin',
    [Platform.GoogleAnalytics]: 'googleanalytics',
    [Platform.TikTokAds]: 'tiktok',
    [Platform.GoogleSearchConsole]: 'googlesearchconsole',
    [Platform.ChatGptAds]: 'chatgpt',
} as const satisfies Record<Platform, string>

/**
 * @description Get the platform name from a specified platform identifier
 */
export const PlatformToFormattedName = {
    [Platform.GoogleAds]: 'Google Ads',
    [Platform.MicrosoftAds]: 'Microsoft Ads',
    [Platform.MetaAds]: 'Meta Ads',
    [Platform.LinkedInAds]: 'LinkedIn Ads',
    [Platform.GoogleAnalytics]: 'Google Analytics',
    [Platform.TikTokAds]: 'TikTok Ads',
    [Platform.GoogleSearchConsole]: 'Google Search Console',
    [Platform.ChatGptAds]: 'ChatGPT Ads',
} as const satisfies Record<Platform, string>

/**
 * @description Platform names in small letters
 */
export type PlatformName = (typeof PlatformToName)[Platform]

const entities = ['adGroup', 'keyword'] as const

/**
 * @description Available entities per platform
 */
export const PlatformAvailableEntities = {
    [Platform.GoogleAds]: {
        adGroup: true,
        keyword: true,
    },
    [Platform.MicrosoftAds]: {
        adGroup: true,
        keyword: true,
    },
    [Platform.MetaAds]: {
        adGroup: true,
        keyword: false,
    },
    [Platform.LinkedInAds]: {
        adGroup: false,
        keyword: false,
    },
    [Platform.TikTokAds]: {
        adGroup: true,
        keyword: true,
    },
    [Platform.GoogleAnalytics]: {
        adGroup: false,
        keyword: false,
    },
    [Platform.GoogleSearchConsole]: {
        adGroup: false,
        keyword: false,
    },
    [Platform.ChatGptAds]: {
        adGroup: true,
        keyword: false,
    },
} as const satisfies Record<Platform, Record<(typeof entities)[number], boolean>>
